import { Box, Divider, Stack, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { useSession } from "@src/appV2/Auth/api/useSession";

import { AccountSwitcherOptionGroupDesktop } from "./AccountSwitcherOptionGroup";
import { type AccountSwitcherUser, type UserDetails } from "../../Shared/AccountSwitcher/types";
import { Button } from "@src/appV2/redesign/components/Button";
import { Text } from "@src/appV2/redesign/components/Text";

interface AccountSwitcherMenuWorkplaceDesktopProps {
  workplaceDetails: UserDetails;
  onUserClicked: (user: AccountSwitcherUser) => void;
  onLogoutClick?: () => void;
}

export function AccountSwitcherMenuWorkplaceDesktop(
  props: AccountSwitcherMenuWorkplaceDesktopProps
) {
  const { workplaceDetails, onUserClicked, onLogoutClick } = props;
  const { profile } = useSession();
  const currentWorkplaceId = profile?.userId;
  const [searchTerm, setSearchTerm] = useState("");

  const filteredWorkplaces = useMemo(() => {
    if (!searchTerm.trim()) {
      return workplaceDetails.list;
    }
    const lowerSearch = searchTerm.toLowerCase();
    return workplaceDetails.list.filter((workplace) =>
      workplace.title.toLowerCase().includes(lowerSearch)
    );
  }, [workplaceDetails.list, searchTerm]);

  const filteredWorkplaceDetails: UserDetails = useMemo(
    () => ({
      ...workplaceDetails,
      list: filteredWorkplaces,
    }),
    [workplaceDetails, filteredWorkplaces]
  );

  return (
    <Stack>
      <Text
        variant="caption"
        sx={(theme) => ({
          color: theme.palette.navbar?.itemContent,
          paddingX: 4,
          paddingY: 2,
          textTransform: "uppercase",
          opacity: 0.7,
        })}
      >
        Workplaces
      </Text>
      {workplaceDetails.list.length > 1 && (
        <>
          <Box sx={{ paddingX: 2, paddingBottom: 2, marginBottom: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search workplaces..."
              autoComplete="off"
              value={searchTerm}
              sx={(theme) => ({
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.background?.inverted?.muted,
                  color: theme.palette.navbar?.itemContentSelected,
                  "& fieldset": {
                    borderColor: theme.palette.button?.outline?.inverted?.normal?.borderColor,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.button?.outline?.inverted?.hover?.borderColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.button?.outline?.inverted?.hover?.borderColor,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  color: theme.palette.navbar?.itemContentSelected,
                  "&::placeholder": {
                    color: theme.palette.navbar?.itemContent,
                    opacity: 1,
                  },
                },
              })}
              onChange={(event) => {
                setSearchTerm(event.target.value);
              }}
            />
          </Box>
        </>
      )}
      <Box
        sx={{
          maxHeight: "400px",
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <AccountSwitcherOptionGroupDesktop
          userDetails={filteredWorkplaceDetails}
          onUserClicked={onUserClicked}
          loadMoreLabel="Load more workplaces"
          selectedUserId={currentWorkplaceId}
        />
      </Box>
      {onLogoutClick && (
        <>
          <Divider
            sx={(theme) => ({
              borderColor: theme.palette.navbar?.itemContent,
              marginY: 2,
              opacity: 0.3,
            })}
          />
          <Text
            variant="caption"
            sx={(theme) => ({
              color: theme.palette.navbar?.itemContent,
              paddingX: 4,
              paddingY: 2,
              textTransform: "uppercase",
              opacity: 0.7,
            })}
          >
            Account
          </Text>
          <Button
            fullWidth
            variant="outlineInverted"
            startIconType="door-straight"
            onClick={onLogoutClick}
            sx={{
              borderRadius: 0,
              border: "none",
              justifyContent: "flex-start",
              paddingY: 3,
              paddingX: 4,
            }}
          >
            Logout
          </Button>
        </>
      )}
    </Stack>
  );
}
